import memoizeOne from 'memoize-one';
import { createSessionStorageProvider } from '@atlassian/jira-browser-storage-providers/src/controllers/session-storage/index.tsx';
import { createLocalExpirableStorageProvider } from '@atlassian/jira-browser-storage-providers/src/controllers/local-storage/index.tsx';
import type { ExpirableStorageNew } from '@atlassian/jira-browser-storage-providers/src/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';

const getStore = memoizeOne(() =>
	fg('onboarding-hub-nth-user-team-type-session-storage')
		? createLocalExpirableStorageProvider('onboarding')
		: createSessionStorageProvider('onboarding'),
);

const ONE_WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000;

type StoredItem = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	value: any;
	accessedAt: number | null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createStoredItem = (value: any, accessedAt?: number): StoredItem => ({
	value,
	accessedAt: accessedAt ?? null,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isItemValid = (item: any): item is StoredItem =>
	typeof item === 'object' &&
	!Array.isArray(item) &&
	item !== null &&
	'value' in item &&
	'accessedAt' in item;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const set = (key: string, value: any): void => {
	const store = getStore();
	const serializedValue = JSON.stringify(createStoredItem(value));
	if (fg('onboarding-hub-nth-user-team-type-session-storage')) {
		const expirationTime = Date.now() + ONE_WEEK_IN_MS;
		store.set(key, serializedValue, expirationTime);
	} else {
		store.set(key, serializedValue);
	}
};
/**
 * Retrieve an item from onboarding browser storage (local storage if onboarding-hub-nth-user-team-type-session-storage is enabled, else session storage).
 * Sets the accessedAt property of this item in storage to Date.now().
 */
export const get = (key: string): StoredItem | null => {
	const store = getStore();
	const item = store.get(key);
	try {
		const parsed = item && JSON.parse(item);
		if (isItemValid(parsed)) {
			const serializedValue = JSON.stringify(createStoredItem(parsed.value, Date.now()));
			if (fg('onboarding-hub-nth-user-team-type-session-storage')) {
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				const currentExpiration = (store as ExpirableStorageNew)?.getExpiration(key);
				if (currentExpiration) {
					store.set(key, serializedValue, currentExpiration);
				} else {
					const expirationTime = Date.now() + ONE_WEEK_IN_MS;
					store.set(key, serializedValue, expirationTime);
				}
			} else {
				store.set(key, serializedValue);
			}
			return parsed;
		}
		return null;
	} catch {
		return null;
	}
};
